/**
 * Under the video (§2.2): chapter header, "Who spoke when" lane, chapter bar + playhead, start/end.
 * DOM, not ECharts: it needs face photos and per-element click/hover. Structure renders once from
 * the store; the playhead, chapter states, "Now playing" title and "Agenda item N of M" follow
 * currentTime through store subscriptions that write to the DOM (no re-render).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { FastForward, Users } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { meetingStore, useMeeting } from "../../stores/meetingStore";
import { useStoreEffect } from "../../hooks/useStoreEffect";
import { Face } from "../shared/Face";
import { clock } from "../../lib/format";
import { chapterAt, skipIfProcedural } from "./skipProcedure";
import { ScrubPreview } from "./ScrubPreview";
import { useScrub } from "./useScrub";
import { chapterReached } from "./vimeoClock";
import type { AgendaChapter, Person, SpeakerTurn } from "../../types";

/**
 * Zoomed into one agenda item (spec 06 card 1; double-click a chapter): the debate's shape, with each
 * turn's face on a stem over the item's own track. Click a face or the track to jump there.
 */
function ZoomedChapter({ chapter, turns, people, onSeek, onExit }: {
  chapter: AgendaChapter; turns: SpeakerTurn[]; people: Record<string, Person>; onSeek: (s: number) => void; onExit: () => void;
}) {
  const fillRef = useRef<HTMLSpanElement>(null);
  const len = Math.max(1, chapter.end - chapter.start);
  const inItem = turns.filter((t) => t.start >= chapter.start && t.start < chapter.end);
  const speakers = new Set(inItem.map((t) => t.personId)).size;
  useStoreEffect(meetingStore, (s) => s.currentTime, (t) => {
    if (fillRef.current) fillRef.current.style.transform = `scaleX(${Math.min(1, Math.max(0, (t - chapter.start) / len))})`;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onExit();
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onExit]);
  return (
    <div className="zoom">
      <div className="zoom-head">
        <span className="zoom-title">{chapter.shortTitle ?? chapter.title}</span>
        <span className="zoom-range">{clock(chapter.start)} – {clock(chapter.end)}</span>
      </div>
      <div className="zoom-lane" aria-label="Who spoke when in this item">
        {inItem.map((t, i) => {
          const name = people[t.personId]?.name ?? "Speaker";
          const tip = `${name} · ${clock(t.start)}–${clock(t.end)}`;
          return (
            <button key={`${t.personId}-${t.start}`} className="lane-face zoom-face" data-row={i % 2}
              style={{ left: `${((t.start - chapter.start) / len) * 100}%`, zIndex: i + 1 }}
              onClick={() => onSeek(t.start)} aria-label={tip} data-tip={tip}>
              <Face name={name} photoUrl={people[t.personId]?.photoUrl} size={18} />
            </button>
          );
        })}
      </div>
      <div className="zoom-track" onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onSeek(chapter.start + ((e.clientX - r.left) / r.width) * len);
      }} onDoubleClick={onExit}>
        <span ref={fillRef} className="zoom-fill" />
      </div>
      <div className="zoom-foot">
        <span className="zoom-count">{inItem.length} {inItem.length === 1 ? "turn" : "turns"} · {speakers} {speakers === 1 ? "speaker" : "speakers"}</span>
        <button className="zoom-level" onClick={onExit} aria-pressed="true">Zoom: this item</button>
      </div>
    </div>
  );
}

/**
 * "Who spoke when" without faces on top of each other: each turn goes in the first of the lane's two rows with room
 * for it (and clear of the public-comment marker); a turn with no room is left out of the lane (it is still in the
 * transcript and People). Positions are in px of the lane's width, so it's laid out again when the width changes.
 */
const FACE = 20, MARKER_W = 72;
function layoutLane(turns: SpeakerTurn[], duration: number, width: number, marker: number | null) {
  if (!width || !duration) return [];
  const rowEnd = [-Infinity, -Infinity];
  const blocked = marker == null ? null : { from: (marker / duration) * width - 2, to: (marker / duration) * width + MARKER_W };
  const out: { turn: SpeakerTurn; row: number; i: number }[] = [];
  turns.forEach((turn, i) => {
    const x = (turn.start / duration) * width, from = x - FACE / 2, to = x + FACE / 2;
    if (blocked && to > blocked.from && from < blocked.to) return;
    const row = rowEnd.findIndex((end) => from >= end + 2);
    if (row < 0) return;
    rowEnd[row] = to;
    out.push({ turn, row, i });
  });
  return out;
}

export function Timeline({ duration, onSeek }: { duration: number; onSeek: (s: number) => void }) {
  const { chapters, turns, skip, people, publicCommenters } = useMeeting(useShallow((s) => ({
    chapters: s.chapters, turns: s.turns, skip: s.skipProcedure, people: s.people, publicCommenters: s.report?.publicCommenters ?? 0,
  })));
  const trackRef = useRef<HTMLDivElement>(null);
  useScrub(trackRef, duration);   // drag along the bar to move through the video; a click seeks to that point
  const laneRef = useRef<HTMLDivElement>(null);
  const [laneW, setLaneW] = useState(0);
  useEffect(() => {
    const el = laneRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setLaneW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [zoom, setZoom] = useState<number | null>(null); // the agenda item zoomed into (double-click)
  const exitZoom = () => setZoom(null);
  const headRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const countRef = useRef<HTMLSpanElement>(null);

  useStoreEffect(meetingStore, (s) => s.currentTime, (t) => {
    const track = trackRef.current, head = headRef.current;
    if (!track || !head) return;
    head.style.left = `${(Math.min(t, duration) / duration) * 100}%`;   // a share of the bar: right at any width
  });
  // The bar is the keyboard slider (←/→, Enter: ScrubPreview); its value is written to the DOM once a second (render rules).
  useStoreEffect(meetingStore, (s) => Math.floor(s.currentTime), (t) => {
    const track = trackRef.current;
    if (!track) return;
    track.setAttribute("aria-valuenow", String(Math.min(t, duration)));
    track.setAttribute("aria-valuetext", clock(Math.min(t, duration)));
  });

  // Chapter states and the header follow the current chapter (changes rarely).
  useStoreEffect(meetingStore, (s) => chapterAt(s.chapters, s.currentTime), (i) => {
    trackRef.current?.querySelectorAll<HTMLElement>(".chapter").forEach((el, k) => {
      el.dataset.state = k < i ? "played" : k === i ? "current" : "upcoming";
    });
    const all = meetingStore.getState().chapters;
    if (titleRef.current) titleRef.current.textContent = i >= 0 ? all[i].shortTitle ?? all[i].title : "";
    if (countRef.current) countRef.current.textContent = i >= 0 ? `Agenda item ${i + 1} of ${all.length}` : `${all.length} agenda items`;
    if (i >= 0) chapterReached(i, all[i].start);
    queueMicrotask(skipIfProcedural); // not inside the store's notify
  });
  useStoreEffect(meetingStore, (s) => s.skipProcedure, (on) => { if (on) queueMicrotask(skipIfProcedural); });

  const publicChapter = chapters.find((c) => /public comment/i.test(c.title));
  const lane = useMemo(() => layoutLane(turns, duration, laneW, publicChapter && publicCommenters > 0 ? publicChapter.start : null),
    [turns, duration, laneW, publicChapter, publicCommenters]);
  return (
    <div className="chapters">
      <div className="chapter-header">
        <div className="now-playing">
          <span className="now-label">Now playing</span>
          <span className="chapter-title" ref={titleRef} />
        </div>
        <div className="chapter-tools">
          <label className="skip-procedure">
            <FastForward size={13} />
            Skip procedure
            <button
              type="button"
              role="switch"
              aria-checked={skip}
              aria-label="Skip procedure"
              className="switch"
              onClick={() => meetingStore.getState().setSkipProcedure(!skip)}
            />
          </label>
          <span className="vdivider" />
          <span className="chapter-count" ref={countRef} />
        </div>
      </div>

      {zoom != null && chapters[zoom] ? (
        <ZoomedChapter chapter={chapters[zoom]} turns={turns} people={people} onSeek={onSeek} onExit={exitZoom} />
      ) : (
        <>
      <div className="speaker-lane" aria-label="Who spoke when" ref={laneRef}>
        {publicChapter && publicCommenters > 0 && (
          <button className="public-marker" style={{ left: `${(publicChapter.start / duration) * 100}%` }} onClick={() => onSeek(publicChapter.start)}>
            <Users size={10} />
            {publicCommenters} public
          </button>
        )}
        {lane.map(({ turn: t, row, i }) => {
          const p = people[t.personId];
          const name = p?.name ?? "Speaker";
          return (
            <button
              key={`${t.personId}-${t.start}`}
              className="lane-face"
              data-row={row}
              style={{ left: `${(t.start / duration) * 100}%`, zIndex: i + 1 }}
              onClick={() => onSeek(t.start)}
              aria-label={`${name} · ${clock(t.start)}–${clock(t.end)}`}
              data-tip={`${name} · ${clock(t.start)}–${clock(t.end)}`}
            >
              <Face name={name} photoUrl={p?.photoUrl} size={18} />
            </button>
          );
        })}
      </div>

      <div ref={trackRef} className="chapter-bar" role="slider" aria-label="Agenda timeline" aria-valuemin={0} aria-valuemax={duration} aria-valuenow={0} tabIndex={0}>
        {/* Chapters are pointer targets inside the slider (keyboard: the slider itself), so they aren't separate tab stops. */}
        {chapters.map((c) => (
          <span
            key={c.start}
            aria-hidden
            className="chapter"
            data-state="upcoming"
            data-procedural={c.procedural || undefined}
            data-skipped={(skip && c.procedural) || undefined}
            style={{ flexGrow: Math.max(1, c.end - c.start), flexBasis: 0 }}
            onDoubleClick={() => { setZoom(chapters.indexOf(c)); onSeek(c.start); }}
          />
        ))}
        <div ref={headRef} className="playhead" />
        <ScrubPreview trackRef={trackRef} duration={duration} />
      </div>
      <div className="bar-ends"><span>{clock(0)}</span><span>{clock(duration)}</span></div>
        </>
      )}
    </div>
  );
}
